import { calculateCardCompletion } from "@shared/field-definitions";
import type { WizardSlice } from "./types";
import { groupTransitionMeasuresByTopic } from "./transitionMeasures.utils";
import type {
  EmployeeSupportData,
  HumanRightsData,
  DiversityInclusionData,
  CommunityContributionData,
  SocialData,
  TransitionMeasure,
} from "@/contexts/wizard/types";

/**
 * Social slice state (all four social cards)
 */
export interface SocialSliceState {
  employeeSupportData: EmployeeSupportData;
  humanRightsData: HumanRightsData;
  diversityInclusionData: DiversityInclusionData;
  communityContributionData: CommunityContributionData;
  socialData: SocialData; // Legacy field for backward compatibility
  // Transition measures, keyed by topicId (employee-support, diversity-inclusion, human-rights)
  transitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
}

/**
 * API response structure for social data
 */
export interface SocialApiData {
  employeeSupportData?: EmployeeSupportData;
  humanRightsData?: HumanRightsData;
  diversityInclusionData?: DiversityInclusionData;
  communityContributionData?: CommunityContributionData;
  socialData?: SocialData;
  transitionMeasures?: TransitionMeasure[];
}

/**
 * State setters for social slice
 */
export interface SocialStateSetter {
  setEmployeeSupportData: (data: EmployeeSupportData) => void;
  setHumanRightsData: (data: HumanRightsData) => void;
  setDiversityInclusionData: (data: DiversityInclusionData) => void;
  setCommunityContributionData: (data: CommunityContributionData) => void;
  setSocialData: (data: SocialData) => void;
  setTransitionMeasuresForTopic: (topicId: string, measures: TransitionMeasure[]) => void;
}

// ==================== API FUNCTIONS ====================

async function fetchSocial(assessmentId: string): Promise<SocialApiData> {
  const response = await fetch(`/api/assessments/${assessmentId}/social`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch social data");
  return response.json();
}

async function saveSocial(assessmentId: string, payload: unknown) {
  return fetch(`/api/assessments/${assessmentId}/social`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
  });
}

// ==================== HYDRATION MAPPER ====================

const defaultEmployeeSupportData: EmployeeSupportData = {
  permanentEmployees: "",
  temporaryEmployees: "",
  femaleEmployees: "",
  maleEmployees: "",
  employeesByCountry: "",
  turnoverRate: "",
  recordableAccidents: "",
  accidentRate: "",
  workRelatedFatalities: "",
  minimumWageCompliance: null,
  collectiveBargainingPercent: "",
  trainingHoursFemale: "",
  trainingHoursMale: "",
  genderPayGapPercent: "",
};

const defaultHumanRightsData: HumanRightsData = {
  hasHumanRightsPolicy: null,
  policyCovers: [],
  hasGrievanceMechanism: null,
  workforceIncidents: null,
  workforceIncidentsDescription: "",
  valueChainIncidents: null,
  valueChainIncidentsDescription: "",
  evidenceFiles: [],
};

const defaultDiversityInclusionData: DiversityInclusionData = {
  femaleManagers: "",
  maleManagers: "",
  selfEmployedWorkers: "",
  temporaryAgencyWorkers: "",
  diversityPolicies: "",
  evidenceFiles: [],
};

const defaultCommunityContributionData: CommunityContributionData = {
  communityPrograms: "",
  charitableGivingAmount: "",
  charitableGivingCurrency: "usd",
  volunteerHours: "",
  measurableOutcomes: "",
  evidenceFiles: [],
};

const defaultSocialData: SocialData = {
  permanentEmployees: "",
  temporaryEmployees: "",
  femaleEmployees: "",
  maleEmployees: "",
  recordableAccidents: "",
  workRelatedFatalities: "",
  safetyTrainingHours: "",
  minimumWageCompliance: null,
  genderPayGapPercent: "",
  collectiveBargainingPercent: "",
  avgTrainingHoursPerEmployee: "",
  femaleManagementPercent: "",
  femaleBoardPercent: "",
  childLaborPolicy: null,
  forcedLaborPolicy: null,
  supplierCodeOfConduct: null,
  humanRightsNotes: "",
  diversityVoiceNote: "",
};

function hydrateSocialState(apiData: SocialApiData, setter: SocialStateSetter): void {
  setter.setEmployeeSupportData(apiData.employeeSupportData || defaultEmployeeSupportData);
  setter.setHumanRightsData(apiData.humanRightsData || defaultHumanRightsData);
  setter.setDiversityInclusionData(apiData.diversityInclusionData || defaultDiversityInclusionData);
  setter.setCommunityContributionData(apiData.communityContributionData || defaultCommunityContributionData);
  setter.setSocialData(apiData.socialData || defaultSocialData);

  // Transition measures: flat array from the API, grouped by topicId for reducer state
  groupTransitionMeasuresByTopic(apiData.transitionMeasures || [], setter.setTransitionMeasuresForTopic);
}

// ==================== SIGNATURE & DIRTY TRACKING ====================

function computeSocialSignature(state: SocialSliceState): string {
  const payload = {
    employeeSupportData: state.employeeSupportData,
    humanRightsData: state.humanRightsData,
    diversityInclusionData: state.diversityInclusionData,
    communityContributionData: state.communityContributionData,
    socialData: state.socialData,
    transitionMeasures: Object.values(state.transitionMeasuresByTopic).flat(),
  };
  return JSON.stringify(payload);
}

function isSocialDirty(state: SocialSliceState, baselineSignature: string): boolean {
  const currentSignature = computeSocialSignature(state);
  return currentSignature !== baselineSignature;
}

// ==================== SAVE ====================

async function saveSocialSlice(assessmentId: string, state: SocialSliceState): Promise<void> {
  const payload = {
    employeeSupportData: state.employeeSupportData,
    humanRightsData: state.humanRightsData,
    diversityInclusionData: state.diversityInclusionData,
    communityContributionData: state.communityContributionData,
    socialData: state.socialData,
    transitionMeasures: Object.values(state.transitionMeasuresByTopic).flat(),
    workforceCompletenessPercent: calculateCardCompletion('employeeSupport', state.employeeSupportData),
    diversityInclusionCompletenessPercent: 100,
    humanRightsCompletenessPercent: calculateCardCompletion('humanRights', state.humanRightsData),
    communityContributionCompletenessPercent: 100,
  };

  const response = await saveSocial(assessmentId, payload);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Failed to save social data" }));
    throw new Error(error.error || "Failed to save social data");
  }
}

// ==================== COMPLETION DATA ====================

function getSocialCompletionData(state: SocialSliceState) {
  return {
    employeeSupportData: state.employeeSupportData,
    humanRightsData: state.humanRightsData,
    diversityInclusionData: state.diversityInclusionData,
    communityContributionData: state.communityContributionData,
    socialData: state.socialData,
  };
}

// ==================== SLICE EXPORT ====================

export const socialSlice: WizardSlice<SocialSliceState, SocialApiData, SocialStateSetter> = {
  queryKey: (assessmentId: string) => ["social", assessmentId],
  fetch: fetchSocial,
  hydrate: hydrateSocialState,
  isDirty: isSocialDirty,
  computeSignature: computeSocialSignature,
  save: saveSocialSlice,
  getCompletionData: getSocialCompletionData,
  impactsCompletion: true,
};
